/*
 * RIDECLOUD VEHICLE CATALOGUE — ZOHO LIVE DATA INTEGRATION
 * Vehicles are fetched live from Zoho CRM via /api/vehicles proxy endpoint.
 * The server-side proxy handles OAuth token refresh automatically.
 * The InterestModal uses the native Zoho Web Form (Method 1) with LEADCF1
 * auto-populated with the selected vehicle name.
 *
 * Filter & Sort (v2):
 *  - Filter sidebar: Make, Vehicle Type, Transmission, Fuel Type, Seats, Price Range
 *  - Sort bar: Price ↑↓, Make A-Z, Model A-Z, Newest
 *  - Active filter chips with individual clear buttons
 *  - Mobile: collapsible filter drawer
 */
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useLocation } from "wouter";
import { ChevronRight, CheckCircle, Loader2, X, SlidersHorizontal, ChevronDown, ChevronUp, ArrowUpDown } from "lucide-react";

// ─── Vehicle type (matches API response from /api/vehicles) ──────────────────
interface Vehicle {
  id: string | number;
  zohoId?: string;
  make: string;
  model: string;
  variant: string;
  type: string;
  transmission: string;
  fuel: string;
  seats: number;
  fromPrice: number;
  km?: number;
  availableKmPacks?: number[];
  kmPricing?: Record<number, { price36: number | null; price24: number | null; price12: number | null }>;
  desc: string;
  image: string;
  colour?: string;
  status?: string;
}

// ─── Fallback vehicles shown while loading or on API error ───────────────────
const fallbackVehicles: Vehicle[] = [
  {
    id: "fallback-1",
    make: "Volkswagen",
    model: "Polo Vivo",
    variant: "1.4 Trendline",
    type: "Hatchback",
    transmission: "Manual",
    fuel: "Petrol",
    seats: 5,
    fromPrice: 4200,
    km: 2000,
    desc: "South Africa's best-selling car — reliable, affordable and practical for everyday driving.",
    image: "/vehicles/polo-vivo.jpg",
  },
  {
    id: "fallback-2",
    make: "Toyota",
    model: "Corolla Cross",
    variant: "1.8 Xi",
    type: "SUV",
    transmission: "Automatic",
    fuel: "Petrol",
    seats: 5,
    fromPrice: 6500,
    km: 2000,
    desc: "Entry-level crossover excellence — Toyota reliability in a modern, stylish package.",
    image: "/vehicles/corolla-cross.jpg",
  },
  {
    id: "fallback-3",
    make: "Suzuki",
    model: "Swift",
    variant: "1.2 GL",
    type: "Hatchback",
    transmission: "Manual",
    fuel: "Petrol",
    seats: 5,
    fromPrice: 3800,
    km: 2000,
    desc: "Compact, fuel-efficient and fun — the perfect city car for urban professionals.",
    image: "/vehicles/swift.jpg",
  },
];

// ─── Sort options ─────────────────────────────────────────────────────────────
type SortKey = "price-asc" | "price-desc" | "make-az" | "model-az" | "newest";
const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "price-asc",  label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "make-az",    label: "Make: A–Z" },
  { value: "model-az",   label: "Model: A–Z" },
  { value: "newest",     label: "Newest First" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function unique<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}

// ─── Interest Modal (fetch-based Zoho submission) ─────────────────────────────
interface InterestModalProps {
  vehicle: Vehicle | null;
  onClose: () => void;
}


const modalInputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.6rem 0.875rem",
  border: "1px solid rgba(46,0,79,0.2)",
  borderRadius: "0.375rem",
  fontSize: "0.875rem",
  color: "#2e004f",
  backgroundColor: "#ffffff",
  outline: "none",
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
};
const modalLabelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "0.8rem",
  fontWeight: 600,
  color: "#2e004f",
  marginBottom: "0.375rem",
};

interface ModalFormState {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  description: string;
  consent: boolean;
}

const defaultModalForm: ModalFormState = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  description: "",
  consent: false,
};

function InterestModal({ vehicle, onClose }: InterestModalProps) {
  const [form, setForm] = useState<ModalFormState>(defaultModalForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when vehicle changes
  useEffect(() => {
    setForm(defaultModalForm);
    setSubmitted(false);
    setError(null);
  }, [vehicle]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      setForm((f) => ({ ...f, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.firstName.trim()) { setError("First Name cannot be empty."); return; }
    if (!form.lastName.trim()) { setError("Last Name cannot be empty."); return; }
    if (!form.phone.trim()) { setError("Tel Number cannot be empty."); return; }
    if (!form.consent) { setError("Please accept the consent checkbox."); return; }
    if (form.email.trim()) {
      const at = form.email.indexOf("@");
      const dot = form.email.lastIndexOf(".");
      if (at < 1 || dot < at + 2 || dot + 2 >= form.email.length) {
        setError("Please enter a valid email address."); return;
      }
    }

    if (!vehicle) return;
    const vehicleName = `${vehicle.make} ${vehicle.model} ${vehicle.variant}`;

    setSubmitting(true);

    try {
      // POST to /api/leads — server handles both Zoho WebToLeadForm and portal logging
      fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          formType: "Contact Me",
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          vehicleOfInterest: vehicleName,
          vehicleInMind: vehicleName,
          description: form.description.trim(),
        }),
      }).catch(() => {});
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!vehicle) return null;
  const vehicleName = `${vehicle.make} ${vehicle.model} ${vehicle.variant}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative rounded-xl overflow-hidden w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        style={{ backgroundColor: "#ffffff", maxWidth: "560px", border: "1px solid rgba(46,0,79,0.12)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div
          className="px-6 pt-5 pb-4 sticky top-0 z-10 border-b"
          style={{ backgroundColor: "#2e004f", borderColor: "rgba(255,255,255,0.1)" }}
        >
          <h3 className="text-lg font-semibold text-white">
            I'm Interested — {vehicle.make} {vehicle.model}
          </h3>
          <p className="text-white/60 text-sm mt-0.5">
            From{" "}
            <span className="text-orange-300 font-semibold">
              R{vehicle.fromPrice.toLocaleString()} pm
            </span>
            {" "}· {vehicle.variant}
          </p>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-all"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6">
          {submitted ? (
            /* ── Thank-you state ── */
            <div className="flex flex-col items-center justify-center text-center py-8 gap-4">
              <CheckCircle className="w-14 h-14" style={{ color: "#e6531e" }} />
              <h3 className="text-xl font-semibold" style={{ color: "#2e004f" }}>
                Thank you, {form.firstName}!
              </h3>
              <p className="text-sm max-w-xs" style={{ color: "#5a3d70" }}>
                We've received your enquiry and a member of our team will be in touch within 24 hours.
              </p>
              <p className="text-xs font-medium px-4 py-2 rounded-full" style={{ backgroundColor: "rgba(230,83,30,0.08)", color: "#e6531e" }}>
                Vehicle: {vehicleName}
              </p>
              <button
                onClick={onClose}
                className="mt-2 w-full py-3 rounded-full text-white font-semibold text-sm"
                style={{ backgroundColor: "#2e004f" }}
              >
                Close
              </button>
            </div>
          ) : (
            /* ── Form state ── */
            <>
              {/* Vehicle confirmation banner */}
              <div
                className="mb-5 px-4 py-3 rounded-lg border"
                style={{ backgroundColor: "rgba(230,83,30,0.06)", borderColor: "rgba(230,83,30,0.25)" }}
              >
                <p className="text-xs font-semibold mb-0.5" style={{ color: "#e6531e" }}>Vehicle of Interest</p>
                <p className="text-sm font-medium" style={{ color: "#2e004f" }}>{vehicleName}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label style={modalLabelStyle}>First Name <span style={{ color: "#e6531e" }}>*</span></label>
                    <input type="text" name="firstName" value={form.firstName} onChange={handleChange} placeholder="First name" style={modalInputStyle} />
                  </div>
                  <div>
                    <label style={modalLabelStyle}>Last Name <span style={{ color: "#e6531e" }}>*</span></label>
                    <input type="text" name="lastName" value={form.lastName} onChange={handleChange} placeholder="Last name" style={modalInputStyle} />
                  </div>
                </div>

                <div>
                  <label style={modalLabelStyle}>Phone <span style={{ color: "#e6531e" }}>*</span></label>
                  <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+27 XX XXX XXXX" style={modalInputStyle} />
                </div>

                <div>
                  <label style={modalLabelStyle}>Email</label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="your@email.com" style={modalInputStyle} />
                </div>

                <div>
                  <label style={modalLabelStyle}>Message</label>
                  <textarea name="description" value={form.description} onChange={handleChange} rows={3} placeholder="Any questions or specific requirements?" style={{ ...modalInputStyle, resize: "vertical" }} />
                </div>

                <div className="flex items-start gap-2 pt-1">
                  <input
                    type="checkbox"
                    name="consent"
                    id="modal_consent"
                    checked={form.consent}
                    onChange={handleChange}
                    className="mt-0.5 w-4 h-4 rounded"
                    style={{ accentColor: "#e6531e", cursor: "pointer" }}
                  />
                  <label htmlFor="modal_consent" className="text-xs leading-relaxed" style={{ color: "#5a3d70", cursor: "pointer" }}>
                    I agree to be contacted regarding this enquiry and consent to the processing of my personal information in accordance with RideCloud's Privacy Policy (POPIA).
                  </label>
                </div>

                {error && (
                  <p className="text-sm font-medium" style={{ color: "#c0392b" }}>{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 rounded-full text-white font-semibold text-sm transition-opacity hover:opacity-90"
                  style={{ backgroundColor: "#e6531e", opacity: submitting ? 0.7 : 1 }}
                >
                  {submitting ? "Sending..." : "Submit Enquiry"}
                </button>

                <p className="text-center text-xs" style={{ color: "#9a8aaa" }}>
                  By submitting, you agree to be contacted about your enquiry.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Filter Sidebar ───────────────────────────────────────────────────────────
interface FilterState {
  makes: string[];
  types: string[];
  transmissions: string[];
  fuels: string[];
  seats: string[];
  priceMin: number;
  priceMax: number;
}

const PRICE_SLIDER_MIN = 0;
const PRICE_SLIDER_MAX = 60000;

const DEFAULT_FILTERS: FilterState = {
  makes: [],
  types: [],
  transmissions: [],
  fuels: [],
  seats: [],
  priceMin: PRICE_SLIDER_MIN,
  priceMax: PRICE_SLIDER_MAX,
};

interface FilterSidebarProps {
  vehicles: Vehicle[];
  filters: FilterState;
  onChange: (f: FilterState) => void;
  onReset: () => void;
  activeCount: number;
}

function FilterSidebar({ vehicles, filters, onChange, onReset, activeCount }: FilterSidebarProps) {
  const makes = useMemo(() => unique(vehicles.map((v) => v.make)).sort(), [vehicles]);
  const types = useMemo(() => unique(vehicles.map((v) => v.type)).sort(), [vehicles]);
  const transmissions = useMemo(() => unique(vehicles.map((v) => v.transmission)).sort(), [vehicles]);
  const fuels = useMemo(() => unique(vehicles.map((v) => v.fuel)).sort(), [vehicles]);
  const seatCounts = useMemo(
    () => unique(vehicles.map((v) => String(v.seats))).sort((a, b) => Number(a) - Number(b)),
    [vehicles]
  );

  function toggle(field: keyof Pick<FilterState, "makes" | "types" | "transmissions" | "fuels" | "seats">, value: string) {
    const current = filters[field] as string[];
    const next = current.includes(value) ? current.filter((x) => x !== value) : [...current, value];
    onChange({ ...filters, [field]: next });
  }

  function chipClass(active: boolean) {
    return [
      "px-3 py-1.5 rounded-full text-xs font-medium border cursor-pointer transition-all select-none",
      active
        ? "text-white border-transparent"
        : "border-[rgba(46,0,79,0.2)] text-[#2e004f] hover:border-[#2e004f]",
    ].join(" ");
  }

  const sectionLabel = "text-xs font-semibold uppercase tracking-wider mb-2 mt-4 first:mt-0";

  return (
    <div className="w-full">
      {/* Header row */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4" style={{ color: "#2e004f" }} />
          <span className="text-sm font-semibold" style={{ color: "#2e004f" }}>Filters</span>
          {activeCount > 0 && (
            <span
              className="w-5 h-5 rounded-full text-white text-xs flex items-center justify-center font-bold"
              style={{ backgroundColor: "#e6531e" }}
            >
              {activeCount}
            </span>
          )}
        </div>
        {activeCount > 0 && (
          <button
            onClick={onReset}
            className="text-xs font-medium underline transition-opacity hover:opacity-70"
            style={{ color: "#e6531e" }}
          >
            Clear all
          </button>
        )}
      </div>

      {/* Make — multi-select dropdown */}
      <p className={sectionLabel} style={{ color: "#7a6a8a" }}>Make</p>
      <div className="relative mb-1">
        <select
          multiple
          size={Math.min(makes.length, 5)}
          value={filters.makes}
          onChange={(e) => {
            const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
            onChange({ ...filters, makes: selected });
          }}
          style={{
            width: "100%",
            border: "1px solid rgba(46,0,79,0.2)",
            borderRadius: "0.375rem",
            fontSize: "0.8rem",
            color: "#2e004f",
            backgroundColor: "#ffffff",
            padding: "0.25rem",
            outline: "none",
          }}
        >
          {makes.map((m) => (
            <option key={m} value={m} style={{ padding: "0.3rem 0.5rem" }}>{m}</option>
          ))}
        </select>
        {filters.makes.length > 0 && (
          <p className="text-xs mt-1" style={{ color: "#9a8aaa" }}>
            {filters.makes.length} selected — hold Ctrl/Cmd to select multiple
          </p>
        )}
        {filters.makes.length === 0 && (
          <p className="text-xs mt-1" style={{ color: "#b0a0c0" }}>Hold Ctrl/Cmd to select multiple</p>
        )}
      </div>

      {/* Vehicle Type */}
      <p className={sectionLabel} style={{ color: "#7a6a8a" }}>Vehicle Type</p>
      <div className="flex flex-wrap gap-1.5 mb-1">
        {types.map((t) => (
          <button
            key={t}
            onClick={() => toggle("types", t)}
            className={chipClass(filters.types.includes(t))}
            style={filters.types.includes(t) ? { backgroundColor: "#2e004f" } : {}}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Transmission */}
      <p className={sectionLabel} style={{ color: "#7a6a8a" }}>Transmission</p>
      <div className="flex flex-wrap gap-1.5 mb-1">
        {transmissions.map((t) => (
          <button
            key={t}
            onClick={() => toggle("transmissions", t)}
            className={chipClass(filters.transmissions.includes(t))}
            style={filters.transmissions.includes(t) ? { backgroundColor: "#2e004f" } : {}}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Fuel Type */}
      <p className={sectionLabel} style={{ color: "#7a6a8a" }}>Fuel Type</p>
      <div className="flex flex-wrap gap-1.5 mb-1">
        {fuels.map((f) => (
          <button
            key={f}
            onClick={() => toggle("fuels", f)}
            className={chipClass(filters.fuels.includes(f))}
            style={filters.fuels.includes(f) ? { backgroundColor: "#2e004f" } : {}}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Seating */}
      <p className={sectionLabel} style={{ color: "#7a6a8a" }}>Seating</p>
      <div className="flex flex-wrap gap-1.5 mb-1">
        {seatCounts.map((s) => (
          <button
            key={s}
            onClick={() => toggle("seats", s)}
            className={chipClass(filters.seats.includes(s))}
            style={filters.seats.includes(s) ? { backgroundColor: "#2e004f" } : {}}
          >
            {s} Seats
          </button>
        ))}
      </div>

      {/* Price Range — dual slider */}
      <p className={sectionLabel} style={{ color: "#7a6a8a" }}>Monthly Price</p>
      <div className="space-y-3 mb-1">
        <div className="flex justify-between text-xs font-medium" style={{ color: "#2e004f" }}>
          <span>R{filters.priceMin.toLocaleString()}</span>
          <span>{filters.priceMax >= PRICE_SLIDER_MAX ? "R60,000+" : `R${filters.priceMax.toLocaleString()}`}</span>
        </div>
        <div className="space-y-2">
          <div>
            <label className="text-xs" style={{ color: "#9a8aaa" }}>Min</label>
            <input
              type="range"
              min={PRICE_SLIDER_MIN}
              max={PRICE_SLIDER_MAX}
              step={500}
              value={filters.priceMin}
              onChange={(e) => {
                const val = Number(e.target.value);
                onChange({ ...filters, priceMin: Math.min(val, filters.priceMax - 500) });
              }}
              style={{ width: "100%", accentColor: "#2e004f" }}
            />
          </div>
          <div>
            <label className="text-xs" style={{ color: "#9a8aaa" }}>Max</label>
            <input
              type="range"
              min={PRICE_SLIDER_MIN}
              max={PRICE_SLIDER_MAX}
              step={500}
              value={filters.priceMax}
              onChange={(e) => {
                const val = Number(e.target.value);
                onChange({ ...filters, priceMax: Math.max(val, filters.priceMin + 500) });
              }}
              style={{ width: "100%", accentColor: "#e6531e" }}
            />
          </div>
        </div>
        {(filters.priceMin > PRICE_SLIDER_MIN || filters.priceMax < PRICE_SLIDER_MAX) && (
          <button
            onClick={() => onChange({ ...filters, priceMin: PRICE_SLIDER_MIN, priceMax: PRICE_SLIDER_MAX })}
            className="text-xs underline transition-opacity hover:opacity-70"
            style={{ color: "#e6531e" }}
          >
            Reset price
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Active Filter Chips ──────────────────────────────────────────────────────
interface ActiveChipsProps {
  filters: FilterState;
  onChange: (f: FilterState) => void;
}

function ActiveChips({ filters, onChange }: ActiveChipsProps) {
  const chips: { label: string; remove: () => void }[] = [];

  filters.makes.forEach((m) =>
    chips.push({ label: m, remove: () => onChange({ ...filters, makes: filters.makes.filter((x) => x !== m) }) })
  );
  filters.types.forEach((t) =>
    chips.push({ label: t, remove: () => onChange({ ...filters, types: filters.types.filter((x) => x !== t) }) })
  );
  filters.transmissions.forEach((t) =>
    chips.push({ label: t, remove: () => onChange({ ...filters, transmissions: filters.transmissions.filter((x) => x !== t) }) })
  );
  filters.fuels.forEach((f) =>
    chips.push({ label: f, remove: () => onChange({ ...filters, fuels: filters.fuels.filter((x) => x !== f) }) })
  );
  filters.seats.forEach((s) =>
    chips.push({ label: `${s} Seats`, remove: () => onChange({ ...filters, seats: filters.seats.filter((x) => x !== s) }) })
  );
  if (filters.priceMin > PRICE_SLIDER_MIN || filters.priceMax < PRICE_SLIDER_MAX) {
    const minLabel = filters.priceMin > 0 ? `R${filters.priceMin.toLocaleString()}` : "R0";
    const maxLabel = filters.priceMax >= PRICE_SLIDER_MAX ? "R60,000+" : `R${filters.priceMax.toLocaleString()}`;
    chips.push({
      label: `${minLabel} – ${maxLabel}`,
      remove: () => onChange({ ...filters, priceMin: PRICE_SLIDER_MIN, priceMax: PRICE_SLIDER_MAX }),
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {chips.map((chip) => (
        <span
          key={chip.label}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium"
          style={{ backgroundColor: "rgba(46,0,79,0.08)", color: "#2e004f" }}
        >
          {chip.label}
          <button
            onClick={chip.remove}
            className="ml-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center hover:bg-[rgba(46,0,79,0.15)] transition-colors"
            aria-label={`Remove ${chip.label} filter`}
          >
            <X className="w-2.5 h-2.5" />
          </button>
        </span>
      ))}
    </div>
  );
}

// ─── Main VehicleCatalogue Component ─────────────────────────────────────────
interface VehicleCatalogueProps {
  featured?: boolean; // If true, show max 6 vehicles, hide filters, show "View All" link
}

export default function VehicleCatalogue({ featured = false }: VehicleCatalogueProps) {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  // Filter & sort state
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [sortKey, setSortKey] = useState<SortKey>("price-asc");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Fetch vehicles from Zoho CRM via server proxy
  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch("/api/vehicles");
        if (!response.ok) throw new Error(`API error: ${response.status}`);
        const data = await response.json() as { vehicles?: Vehicle[] };
        if (data.vehicles && data.vehicles.length > 0) {
          setVehicles(data.vehicles);
        } else {
          setVehicles(fallbackVehicles);
        }
      } catch (err) {
        console.error("Failed to fetch vehicles:", err);
        setError("Unable to load live catalogue. Showing sample vehicles.");
        setVehicles(fallbackVehicles);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, []);

  // Active filter count (for badge)
  const activeFilterCount = useMemo(() => {
    return (
      filters.makes.length +
      filters.types.length +
      filters.transmissions.length +
      filters.fuels.length +
      filters.seats.length +
      ((filters.priceMin > PRICE_SLIDER_MIN || filters.priceMax < PRICE_SLIDER_MAX) ? 1 : 0)
    );
  }, [filters]);

  // Apply filters + sort
  const displayedVehicles = useMemo(() => {
    let result = [...vehicles];

    if (filters.makes.length > 0) result = result.filter((v) => filters.makes.includes(v.make));
    if (filters.types.length > 0) result = result.filter((v) => filters.types.includes(v.type));
    if (filters.transmissions.length > 0) result = result.filter((v) => filters.transmissions.includes(v.transmission));
    if (filters.fuels.length > 0) result = result.filter((v) => filters.fuels.includes(v.fuel));
    if (filters.seats.length > 0) result = result.filter((v) => filters.seats.includes(String(v.seats)));
    if (filters.priceMin > PRICE_SLIDER_MIN || filters.priceMax < PRICE_SLIDER_MAX) {
      result = result.filter((v) => v.fromPrice >= filters.priceMin && v.fromPrice <= filters.priceMax);
    }

    // Sort
    result.sort((a, b) => {
      switch (sortKey) {
        case "price-asc":  return a.fromPrice - b.fromPrice;
        case "price-desc": return b.fromPrice - a.fromPrice;
        case "make-az":    return a.make.localeCompare(b.make);
        case "model-az":   return a.model.localeCompare(b.model);
        case "newest":     return String(b.id).localeCompare(String(a.id));
        default:           return 0;
      }
    });

    // In featured mode, limit to first 6 vehicles (no filter/sort)
    if (featured) return result.slice(0, 6);
    return result;
  }, [vehicles, filters, sortKey, featured]);

  const [, navigate] = useLocation();

  const resetFilters = () => setFilters(DEFAULT_FILTERS);

  return (
    <section id="vehicles" className="py-16 md:py-20" style={{ backgroundColor: "#f8f7fa" }}>
      <div className="container">
        {/* ── Section Header ── */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h2 className="text-3xl md:text-4xl font-semibold mb-2" style={{ color: "#2e004f" }}>
              {featured ? "Featured Vehicles" : "Browse Vehicles"}
            </h2>
            <p className="text-sm" style={{ color: "#5a3d70", maxWidth: "600px" }}>
              {featured
                ? "All RideCloud subscriptions include comprehensive insurance, licensing, routine maintenance, tracking and 24/7 roadside assistance. Excludes fuel and tolls."
                : "Find the right vehicle for your lifestyle. Filter by make, type, transmission, fuel, seating and price. All subscriptions include comprehensive insurance, licensing, routine maintenance, tracking and 24/7 roadside assistance. Excludes fuel and tolls."}
            </p>
            {error && (
              <p className="text-xs mt-1" style={{ color: "#e6531e" }}>{error}</p>
            )}
          </div>
          {featured && (
            <a
              href="/vehicles"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold text-white flex-shrink-0 transition-opacity hover:opacity-90"
              style={{ backgroundColor: "#2e004f" }}
            >
              View all vehicles
            </a>
          )}
        </div>

        {/* ── Layout: Sidebar + Grid ── */}
        <div className="flex flex-col lg:flex-row gap-8">

          {/* ── Filter Sidebar (desktop) ── */}
          <aside
            className={featured ? "hidden" : "hidden lg:block flex-shrink-0 rounded-xl p-5"}
            style={{
              width: "240px",
              backgroundColor: "#ffffff",
              border: "1px solid rgba(46,0,79,0.1)",
              alignSelf: "flex-start",
              position: "sticky",
              top: "80px",
            }}
          >
            <FilterSidebar
              vehicles={vehicles}
              filters={filters}
              onChange={setFilters}
              onReset={resetFilters}
              activeCount={activeFilterCount}
            />
          </aside>

          {/* ── Main content area ── */}
          <div className="flex-1 min-w-0">

            {/* Sort bar + mobile filter toggle */}
            {!featured && <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              {/* Left: result count + active chips */}
              <div>
                {!loading && (
                  <p className="text-sm font-medium" style={{ color: "#5a3d70" }}>
                    {displayedVehicles.length === vehicles.length
                      ? `${vehicles.length} vehicle${vehicles.length !== 1 ? "s" : ""} available`
                      : `${displayedVehicles.length} of ${vehicles.length} vehicles`}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* Mobile filter button */}
                <button
                  onClick={() => setMobileFiltersOpen((o) => !o)}
                  className="lg:hidden flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-all"
                  style={{
                    borderColor: activeFilterCount > 0 ? "#e6531e" : "rgba(46,0,79,0.2)",
                    color: activeFilterCount > 0 ? "#e6531e" : "#2e004f",
                    backgroundColor: activeFilterCount > 0 ? "rgba(230,83,30,0.06)" : "transparent",
                  }}
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span
                      className="w-4 h-4 rounded-full text-white text-xs flex items-center justify-center font-bold"
                      style={{ backgroundColor: "#e6531e" }}
                    >
                      {activeFilterCount}
                    </span>
                  )}
                </button>

                {/* Sort dropdown */}
                <div className="relative">
                  <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium"
                    style={{ borderColor: "rgba(46,0,79,0.2)", color: "#2e004f", backgroundColor: "#ffffff" }}
                  >
                    <ArrowUpDown className="w-3.5 h-3.5 flex-shrink-0" />
                    <select
                      value={sortKey}
                      onChange={(e) => setSortKey(e.target.value as SortKey)}
                      className="bg-transparent border-none outline-none text-sm font-medium cursor-pointer pr-1"
                      style={{ color: "#2e004f", minWidth: "140px" }}
                    >
                      {SORT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>}

            {/* Mobile filter drawer */}
            {!featured && mobileFiltersOpen && (
              <div
                className="lg:hidden mb-5 rounded-xl p-5"
                style={{ backgroundColor: "#ffffff", border: "1px solid rgba(46,0,79,0.1)" }}
              >
                <FilterSidebar
                  vehicles={vehicles}
                  filters={filters}
                  onChange={setFilters}
                  onReset={resetFilters}
                  activeCount={activeFilterCount}
                />
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="mt-4 w-full py-2.5 rounded-full text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: "#2e004f" }}
                >
                  Show {displayedVehicles.length} result{displayedVehicles.length !== 1 ? "s" : ""}
                </button>
              </div>
            )}

            {/* Active filter chips */}
            {!featured && <ActiveChips filters={filters} onChange={setFilters} />}

            {/* Loading state */}
            {loading && (
              <div className="flex items-center justify-center py-20 gap-3" style={{ color: "#2e004f" }}>
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-sm font-medium">Loading available vehicles...</span>
              </div>
            )}

            {/* Empty state */}
            {!loading && displayedVehicles.length === 0 && (
              <div
                className="flex flex-col items-center justify-center py-20 rounded-xl text-center"
                style={{ backgroundColor: "#ffffff", border: "1px solid rgba(46,0,79,0.1)" }}
              >
                <SlidersHorizontal className="w-10 h-10 mb-3 opacity-30" style={{ color: "#2e004f" }} />
                <p className="text-base font-semibold mb-1" style={{ color: "#2e004f" }}>No vehicles match your filters</p>
                <p className="text-sm mb-4" style={{ color: "#7a6a8a" }}>Try adjusting or clearing your filters to see more results.</p>
                <button
                  onClick={resetFilters}
                  className="px-5 py-2 rounded-full text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: "#e6531e" }}
                >
                  Clear Filters
                </button>
              </div>
            )}

            {/* Vehicle Grid */}
            {!loading && displayedVehicles.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {displayedVehicles.map((vehicle) => (
                  <div
                    key={vehicle.id}
                    className="rounded-lg overflow-hidden bg-white flex flex-col"
                    style={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: "5.625px" }}
                  >
                    {/* Image */}
                    <div className="relative" style={{ height: "200px", overflow: "hidden" }}>
                      <img
                        src={vehicle.image}
                        alt={`${vehicle.make} ${vehicle.model}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = "/vehicles/polo-vivo.jpg";
                        }}
                      />
                    </div>
                    {/* Content */}
                    <div className="p-4 flex flex-col flex-1">
                      <h3 className="text-lg font-semibold mb-0.5" style={{ color: "#2e004f" }}>
                        {vehicle.make} {vehicle.model}
                      </h3>
                      <p className="text-sm mb-3" style={{ color: "#7a6a8a" }}>
                        {vehicle.variant}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {[vehicle.type, vehicle.transmission, vehicle.fuel, `${vehicle.seats} Seats`].map((spec) => (
                          <span key={spec} className="tag-pill">{spec}</span>
                        ))}
                      </div>
                      <p className="text-sm mb-4 leading-relaxed flex-1" style={{ color: "#5a3d70" }}>
                        {vehicle.desc}
                      </p>
                      <div className="mb-4">
                        <p className="text-base font-bold" style={{ color: "#2e004f" }}>
                          {vehicle.fromPrice > 0
                            ? `From R${vehicle.fromPrice.toLocaleString()} pm`
                            : "Contact us for pricing"}
                        </p>
                        <p className="text-sm" style={{ color: "#9a8aaa" }}>
                          {vehicle.availableKmPacks && vehicle.availableKmPacks.length > 0
                            ? vehicle.availableKmPacks.map(k => `${k.toLocaleString()} km`).join(" · ")
                            : vehicle.km
                              ? `${vehicle.km.toLocaleString()} km / month`
                              : "Multiple km packs"}
                        </p>
                      </div>
                      <button
                        onClick={() => navigate(`/vehicle/${vehicle.id}`)}
                        data-vehicle-name={`${vehicle.make} ${vehicle.model} ${vehicle.variant}`}
                        className="im-interested-button btn-orange w-full justify-center"
                      >
                        I'm interested
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Bottom View All (featured mode only) ── */}
      {featured && (
        <div className="mt-10 flex justify-center">
          <a
            href="/vehicles"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold text-white flex-shrink-0 transition-opacity hover:opacity-90"
            style={{ backgroundColor: "#2e004f" }}
          >
            View all vehicles
          </a>
        </div>
      )}

      {/* Interest Modal — Zoho Web Form */}
      {selectedVehicle && (
        <InterestModal
          vehicle={selectedVehicle}
          onClose={() => setSelectedVehicle(null)}
        />
      )}
    </section>
  );
}
